import { useContext, useEffect } from 'react'
import {
  WebSocketContext,
  type WebSocketContextValue,
  type WebSocketMessage,
} from '@/providers/websocket'

/**
 * Hook to access the WebSocket context
 * @throws Error if used outside of WebSocketProvider
 */
export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext)

  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }

  return context
}

/**
 * Hook to subscribe to a specific WebSocket event
 * Automatically cleans up subscription on unmount
 */
export function useWebSocketEvent<T = unknown>(
  eventType: string,
  handler: (message: WebSocketMessage<T>) => void
): void {
  const { subscribe } = useWebSocket()

  useEffect(() => {
    return subscribe<T>(eventType, handler)
  }, [eventType, handler, subscribe])
}

/**
 * Hook to get the current connection status
 */
export function useWebSocketStatus() {
  const { status, isConnected, connectionId, reconnectAttempts, lastError } = useWebSocket()

  return {
    status,
    isConnected,
    connectionId,
    reconnectAttempts,
    lastError,
  }
}
